import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  HeaderContainer,
  Logo,
  LogoImage,
  IconWrapper,
  SubIcon,
  SearchContainer,
  SearchInput,
  ProfileImage,
  ProfileContainer,
  CreditsBadgeOverlay,
  RecentSearchesPopup,
  RecentSearchItem,
  RecentSearchIcon,
  RecentSearchText,
  SearchInputWrapper,
  RewardWrapper,
  RewardTextContainer,
  LangWrapper,
  InfoWrapper,
  SelectedLang,
  LanguageDropdown,
  LangMenu,
  SubMenu,
  NotificationButton,
  DashbordIconWrapper
} from './styles'
import {
  bell,
  creditsIcon,
  accountIcon,
  search,
  SearchIcon,
  universeMobileLogo,
  eternalLogo,
  RewardsIcon,
  credits_18,
  PlainCoinIcon,
  SelectLangIcon,
  NotificationIcon,
  FaqIcon,
  DashboardNavIcon,
  ENLogoIcon
} from 'shared-ui/assets'
import {
  useUserProfile,
  useResponsive,
  useSubscriptionButtonStatus,
  useCoinBalance,
  useSearchAll,
  useCreditBalance,
  useLanguages,
  useNotificationData
} from 'shared-ui/hooks'
import { enforceInputMaxLength } from 'shared-ui/utils/helper'
import { media } from '../../utils/media'
import {
  addRecentSearch,
  getGuestUser,
  getRecentSearches,
  getLastViewedNotificationTime,
  setLastViewedNotificationTime,
  getLastViewedNotificationId,
  setLastViewedNotificationId
} from '../../utils/localStorage'
import { dismissAllJobs } from 'shared-ui/redux/reducers/jobsReducer'
import { Button, LazyImage, GuestUserModal, BackButton } from 'shared-ui/components'
import { useTheme } from 'styled-components'
import { ROUTES, SEARCH_COPY,BUTTON_LABELS} from 'shared-ui/utils/constants'
import { GA_EVENTS } from 'shared-ui/utils/gaConstants'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'
import { useLanguage } from 'shared-ui/utils/languageProvider'
import { sanitizeSearchInput } from 'shared-ui/utils/sanitizeSearchInput'
import { formatCredits } from 'shared-ui/utils/helper'
import { getOptimizedAvatarUrl } from 'shared-ui/utils/performanceUtils'

const Header =({ logo,onOpenInfoModal  }) => {
  const user = useSelector((state) => state.auth.user)
  const accessToken = localStorage.getItem('access')
  const { data: profilData } = useUserProfile(user?.user_id, {
    enabled: !!user?.user_id && !!accessToken
  })
  const [isFocused, setIsFocused] = useState(false)
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const trackEvent = useTrackEvent()
  const isMobile = useResponsive(media.mobile)
  const navigate = useNavigate()
  const theme = useTheme()
  const [searchQuery, setSearchQuery] = useState('')
  const location = useLocation()
  const isSearchPage = location.pathname === ROUTES.sSearch
  const isSuperHomePage = location.pathname
    .toLowerCase()
    .includes('/super-app-home')
  const [showRecentSearches, setShowRecentSearches] = useState(false)
  const [recent, setRecent] = useState([])
  const searchInputRef = useRef(null)
  const { data: buttonStatus } = useSubscriptionButtonStatus(isAuthenticated)
  const [showGuestModal, setShowGuestModal] = useState(false)
  const isEternalPage = location.pathname.toLowerCase().includes('eternal')
  const [isEternalContext, setIsEternalContext] = useState(false)
  const [searchSuggestions, setSearchSuggestions] = useState([])
  const { language,toggleLanguage } = useLanguage()
  const  isInAiPage = location.pathname.includes('/ai-tool')
  const headerLogo = isEternalPage ? eternalLogo : logo || universeMobileLogo
  const [open,setOpen] =useState(false)
  const shouldSearch = sanitizeSearchInput(searchQuery).trim().length >= 3
  const memoizedQuery = useMemo(
    () => (shouldSearch ? sanitizeSearchInput(searchQuery).trim() : ''),
    [searchQuery, shouldSearch]
  )
  const { data, loading } = useSearchAll(memoizedQuery)
  const { data: langData } = useLanguages()
  const wrapperRef = useRef(null)
  
  const handleSelect = (langKey) => {
    toggleLanguage(langKey)
    setOpen(false)
  }
  const handleClickDashboard = useCallback(() => {
    navigate('/dashboard', {
      state: {
        prevLocation: location.pathname
      }
    })
  }, [navigate, location.pathname])

 useEffect(() => {
  const handleClickOutside = (event) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target)
    ) {
      setOpen(false);
    }

  };

  if (open) {
    document.addEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [open]);
  
  useEffect(() => {
    // enforce maxlength even when scripts set input.value directly
    enforceInputMaxLength()
    if (!data?.data) return
    const result = data.data
    const toolNames = result?.Tools?.items?.map((item) => item.name) || []

    const postTitles =
      result?.Posts?.items?.map((item) => item.title?.trim()) || []

    const CreatorsName =
      result?.Creators?.items?.map((item) => item.username?.trim()) || []

    const PlaylistsName =
      result?.Playlists?.items?.map((item) => item.genre?.trim()) || []

    const combined = [
      ...toolNames,
      ...postTitles,
      ...CreatorsName,
      ...PlaylistsName
    ]
      .filter(Boolean)
      .map((t) => t.trim())

    const unique = Array.from(new Set(combined)).map((t) =>
      sanitizeSearchInput(t)
    )

    setSearchSuggestions(unique)
  }, [data, language])

  useEffect(() => {
    if (isEternalPage) setIsEternalContext(true)
  }, [isEternalPage, location.pathname])

  useEffect(() => {
    setRecent(getRecentSearches())
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowRecentSearches(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLogoClick = useCallback(() => {
    navigate(ROUTES.home)
  }, [navigate])

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault()
      const trimmedQuery = sanitizeSearchInput(searchQuery).trim()
      if (trimmedQuery) {
        addRecentSearch(trimmedQuery)
        setRecent(getRecentSearches())
        if (!isSuperHomePage) {
          navigate(`${ROUTES.search}?q=${encodeURIComponent(trimmedQuery)}`, { state: { query: trimmedQuery } })
        } else {
          navigate(`${ROUTES.sSearch}?q=${encodeURIComponent(trimmedQuery)}`, { state: { query: trimmedQuery } })
        }
        setSearchQuery('')
        setShowRecentSearches(false)
      }
    },
    [searchQuery, isSuperHomePage, navigate]
  )

  const handleRecentSearchClick = useCallback(
    (term) => {
      const safe = sanitizeSearchInput(term)
      setSearchQuery(safe)
      addRecentSearch(safe)
      setRecent(getRecentSearches())
      if (!isSuperHomePage) {
        navigate(`${ROUTES.search}?q=${encodeURIComponent(safe)}`, { state: { query: safe } })
      } else {
        navigate(`${ROUTES.sSearch}?q=${encodeURIComponent(safe)}`, { state: { query: safe } })
      }
      setShowRecentSearches(false)
    },
    [isSuperHomePage, navigate]
  )

  const handleSuggestionClick = useCallback(
    (title) => {
      const safe = sanitizeSearchInput(title)
      setSearchQuery(safe)
      addRecentSearch(safe)
      setRecent(getRecentSearches())
      if (!isSuperHomePage) {
        navigate(`${ROUTES.search}?q=${encodeURIComponent(safe)}`, { state: { query: safe } })
      } else {
        navigate(`${ROUTES.sSearch}?q=${encodeURIComponent(safe)}`, { state: { query: safe } })
      }
      setShowRecentSearches(false)
    },
    [isSuperHomePage, navigate]
  )

  const { data: creditData } = useCreditBalance()
  const balanceCredit =
    formatCredits(Number(creditData?.data?.available_credits)) || 0

  const { data: coinData } = useCoinBalance()
  const balanceCoin =
    Number(
      coinData?.data?.balance?.map((balance) => balance.amount).toString()
    ) || 0

  const isGuestuser = getGuestUser() === 'true'
  const dispatch = useDispatch()
  const activeJobs = useSelector((state) => state.jobs?.activeJobs || {})
  const isNotificationsQueryEnabled = Boolean(isAuthenticated && !isGuestuser && user?.user_id)
  const { data: notificationData } = useNotificationData(
    isNotificationsQueryEnabled ? user?.user_id : null,
    '',
    { refetchInterval: 30000 }
  )

  const [viewedTimestamp, setViewedTimestamp] = useState(() =>
    getLastViewedNotificationTime(user?.user_id)
  )
  const [lastViewedId, setLastViewedId] = useState(() =>
    getLastViewedNotificationId(user?.user_id)
  )
  const [hasForegroundNotif, setHasForegroundNotif] = useState(false)

  useEffect(() => {
    setViewedTimestamp(getLastViewedNotificationTime(user?.user_id))
    setLastViewedId(getLastViewedNotificationId(user?.user_id))
  }, [user?.user_id])

  const markNotificationsAsViewed = useCallback(() => {
    const notifs = (notificationData?.data || []).filter(
      (n) => n && n.is_deleted !== true && n.is_deleted !== 'true'
    )
    const maxTime = notifs.reduce((acc, n) => {
      const t = n?.created_at ? new Date(n.created_at).getTime() : 0
      return t > acc ? t : acc
    }, 0)
    const now = Math.max(Date.now(), maxTime)
    const latestId = notifs[0]?._id

    setLastViewedNotificationTime(user?.user_id, now)
    if (latestId) {
      setLastViewedNotificationId(user?.user_id, latestId)
      setLastViewedId(latestId)
    }
    setViewedTimestamp(now)
    setHasForegroundNotif(false)
    dispatch(dismissAllJobs())

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('NOTIFICATIONS_VIEWED', {
          detail: { timestamp: now, id: latestId }
        })
      )
    }
  }, [user?.user_id, notificationData?.data, dispatch])

  useEffect(() => {
    const handleNotifViewed = (e) => {
      const time = e?.detail?.timestamp || Date.now()
      const id = e?.detail?.id || getLastViewedNotificationId(user?.user_id)
      setViewedTimestamp(time)
      setLastViewedId(id)
      setHasForegroundNotif(false)
    }

    const handleNewNotif = () => {
      setHasForegroundNotif(true)
    }

    const handleStorage = (e) => {
      if (
        e.key === 'last_viewed_notification_timestamp' ||
        (user?.user_id && e.key === `last_viewed_notification_timestamp_${user.user_id}`)
      ) {
        setViewedTimestamp(getLastViewedNotificationTime(user?.user_id))
        setLastViewedId(getLastViewedNotificationId(user?.user_id))
        setHasForegroundNotif(false)
      }
    }

    window.addEventListener('NOTIFICATIONS_VIEWED', handleNotifViewed)
    window.addEventListener('NEW_NOTIFICATION_RECEIVED', handleNewNotif)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener('NOTIFICATIONS_VIEWED', handleNotifViewed)
      window.removeEventListener('NEW_NOTIFICATION_RECEIVED', handleNewNotif)
      window.removeEventListener('storage', handleStorage)
    }
  }, [user?.user_id])

  useEffect(() => {
    if (location.pathname === ROUTES.notifications && isAuthenticated && !isGuestuser) {
      markNotificationsAsViewed()
    }
  }, [location.pathname, isAuthenticated, isGuestuser, markNotificationsAsViewed])

  const hasUnread = useMemo(() => {
    if (!isAuthenticated || isGuestuser || location.pathname === ROUTES.notifications) {
      return false
    }

    if (hasForegroundNotif) {
      return true
    }

    const hasUnviewedCompletedJob = Object.values(activeJobs || {}).some(
      (j) => j && j.status === 'completed' && !j.viewed && !j.dismissed
    )
    if (hasUnviewedCompletedJob) {
      return true
    }

    const remoteList = (notificationData?.data || []).filter(
      (item) => item && item.is_deleted !== true && item.is_deleted !== 'true'
    )
    if (remoteList.length > 0) {
      if (!viewedTimestamp && !lastViewedId) {
        return true
      }

      return remoteList.some((item) => {
        if (item.is_read === false || item.read === false || item.viewed === false) {
          return true
        }
        const itemTime = item.created_at ? new Date(item.created_at).getTime() : 0
        if (itemTime && viewedTimestamp && itemTime > viewedTimestamp) {
          return true
        }
        if (lastViewedId && item._id && Number(item._id) > Number(lastViewedId)) {
          return true
        }
        return false
      })
    }

    return false
  }, [
    isAuthenticated,
    isGuestuser,
    location.pathname,
    hasForegroundNotif,
    activeJobs,
    notificationData?.data,
    viewedTimestamp,
    lastViewedId
  ])

  const rawProfilePic =
    profilData?.data?.image ||
    profilData?.data?.profile_picture ||
    profilData?.data?.thumbnail ||
    accountIcon

  const profilePic = useMemo(
    () => getOptimizedAvatarUrl(rawProfilePic, isMobile ? 32 : 40),
    [rawProfilePic, isMobile]
  )

  const handleProfileImageError = (e) => {
    e.target.src = accountIcon
  }

  const onProfileClick = () => {
    navigate(
      isEternalPage || isEternalContext
        ? '/eternal/userProfile'
        : ROUTES.profile || '/profile',
      { state: { from: location.pathname } }
    )
  }

  return (
    <>
      {isMobile ? (
        <HeaderContainer isMobile={isMobile}>
          <Logo
          // onClick={handleLogoClick}
          >
            {location.pathname !== ROUTES.sAppHome && (
              <BackButton pathLeft={ROUTES.sAppHome} />
            )}
            <LogoImage src={headerLogo} alt="Eros Universe Logo" />
          </Logo>
          <IconWrapper isMobile={isMobile}>
            {/* {isGuestuser == 'true' ? ( */}
            {isGuestuser ? (
              <Button
                gradientButton
                fontSize={'12px'}
                fontWeight={600}
                color={theme.colors.White}
                padding={'5px 8px'}
                onClick={() => {
                  setShowGuestModal(true)
                }}
              >
              {BUTTON_LABELS.SUBSCRIBE}
              </Button>
            ) : (
              buttonStatus &&
              isAuthenticated &&
              buttonStatus?.data?.state != 'Subscribed' &&
              (!isEternalPage || !isEternalContext) && (
                <Button
                  gradientButton
                  fontSize={'12px'}
                  fontWeight={600}
                  color={theme.colors.White}
                  padding={'5px 8px'}
                  onClick={() => {
                    trackEvent(GA_EVENTS.subscription_page_viewed, {
                      source: 'paywall'
                    })
                    const isBuyExtraCredits = buttonStatus?.data?.key === 'Buy Credits'
                    navigate(ROUTES.subscription, {
                      state: isBuyExtraCredits ? { fromWalletTopUp: 'topup' } : {}
                    })
                  }}
                >
                  {buttonStatus?.data?.state}
                </Button>
              )
            )}
            {!isSearchPage && (!isEternalPage || !isEternalContext) && (
              <div
                className="cursor-pointer mr-3"
                onClick={() => {
                  trackEvent(GA_EVENTS.search_opened, {
                    source:
                      location.pathname == ROUTES.sAppHome ? 'home' : 'discover'
                  })

                  if (!isSuperHomePage) {
                    navigate(ROUTES.search)
                  } else {
                    navigate(ROUTES.sSearch)
                  }
                }}
              >
                <SubIcon src={search} alt="search" />
              </div>
            )}
            <div
              className="cursor-pointer mr-3 relative"
              onClick={() => {
                if (isGuestuser) {
                  setShowGuestModal(true)
                } else {
                  markNotificationsAsViewed()
                  trackEvent(GA_EVENTS.notifications_opened, {
                    source: 'top_nav'
                  })
                  navigate(ROUTES.notifications, {
                    state: { location: isEternalPage ? 'eternal' : null }
                  })
                }
              }}
            >
              <SubIcon src={bell} alt="notification" />
              {hasUnread && (
                <span
                  id="header-notification-badge-mobile"
                  className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-[#FF3B30] rounded-full pointer-events-none"
                  style={{
                    boxShadow: '0 0 0 2px #000, 0 0 6px rgba(255, 59, 48, 0.6)'
                  }}
                />
              )}
            </div>
            <ProfileContainer
              className="cursor-pointer"
              onClick={() => onProfileClick()}
            >
              <ProfileImage
                src={profilePic}
                alt="Profile"
                isMobile={true}
                width={32}
                height={32}
                onError={handleProfileImageError}
              />
              {balanceCredit > 0 && (
                <CreditsBadgeOverlay isMobile={isMobile}>
                  <LazyImage
                    src={credits_18}
                    alt="credits"
                    style={{ height: '15px', width: '15px' }}
                  />
                  <span className="mx-0.5 text-[10px] font-semibold">
                    {formatCredits(balanceCredit || 0)}

                  </span>
                </CreditsBadgeOverlay>
              )}
            </ProfileContainer>
          </IconWrapper>
          {showGuestModal && (
            <GuestUserModal onClose={() => setShowGuestModal(false)} />
          )}
        </HeaderContainer>
      ) : (
        <HeaderContainer>
          {/* Search Bar */}
            <div className='flex items-center'>
              <DashbordIconWrapper className="mr-2">
                <DashboardNavIcon className="cursor-pointer" onClick={handleClickDashboard} />
              </DashbordIconWrapper>
              <ENLogoIcon className="cursor-pointer" onClick={() => navigate(ROUTES.sAppHome)} />
            </div>
          <div>{!isInAiPage &&<>
           {!isSearchPage && (!isEternalPage || !isEternalContext) && (
            <SearchInputWrapper ref={searchInputRef}>
              <SearchContainer
                onSubmit={handleSearchSubmit}
                $isFocused={isFocused}
              >
              <SearchIcon className="ml-4"/>
                <SearchInput
                  type="text"
                  placeholder={SEARCH_COPY.placeholder}
                  value={searchQuery}
                  maxLength={100}
                  onChange={(e) =>
                    setSearchQuery(sanitizeSearchInput(e.target.value))
                  }
                  onFocus={() => {
                    setShowRecentSearches(true)
                    setIsFocused(true)
                  }}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit(e)
                    }
                  }}
                />
              </SearchContainer>

              {showRecentSearches &&
                recent.length > 0 &&
                searchQuery.trim() === '' && (
                  <RecentSearchesPopup>
                    {recent.map((term) => (
                      <RecentSearchItem
                        key={term}
                        onClick={() => handleRecentSearchClick(term)}
                      >
                        <RecentSearchIcon src={search} alt="Search" />
                        <RecentSearchText>{term}</RecentSearchText>
                      </RecentSearchItem>
                    ))}
                  </RecentSearchesPopup>
                )}

              {showRecentSearches &&
                searchQuery.trim().length >= 3 &&
                searchSuggestions.length > 0 && (
                  <RecentSearchesPopup>
                    {searchSuggestions.map((title) => (
                      <RecentSearchItem
                        key={title}
                        onClick={() => handleSuggestionClick(title)}
                      >
                        <RecentSearchIcon src={search} alt="Search" />
                        <RecentSearchText>{title}</RecentSearchText>
                      </RecentSearchItem>
                    ))}
                  </RecentSearchesPopup>
                )}
            </SearchInputWrapper>
          )}
          </>}
          </div>
          <div className='flex ml-10'>
          {isGuestuser ? (
                <Button
                  gradientButton
                  fontSize={'14px'}
                  height="36px"
                  lineHeight="20px"
                  header={true}
                  fontWeight={600}
                  color={theme.colors.White}
                  padding={'8px 20px'}
                  borderRadius={"8px"}
                  flexShrink={0}
                  boxShadow="0 4px 6px rgba(0,0,0,0.1)"
                  hoverEffect
                  onClick={() => {
                    setShowGuestModal(true)
                  }}
                >
                  {BUTTON_LABELS.SUBSCRIBE}
                </Button>
          ) : (
            buttonStatus &&
            isAuthenticated &&
            buttonStatus?.data?.state != 'Subscribed' &&
            (!isEternalPage || !isEternalContext) && (
              <Button
                gradientButton
                fontSize={'14px'}
                lineHeight="20px"
                height="36px"
                header={true}
                fontWeight={600}
                color={theme.colors.White}
                padding={'6px 20px'}
                borderRadius={"8px"}
                flexShrink={0}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 4px 6px rgba(0,0,0,0.1)"
                hoverEffect
                onClick={() => {
                  trackEvent(GA_EVENTS.subscription_page_viewed, {
                    source: 'paywall'
                  })
                  const isBuyExtraCredits = buttonStatus?.data?.key === 'Buy Credits'
                  navigate(ROUTES.subscription, {
                    state: isBuyExtraCredits ? { fromWalletTopUp: 'topup' } : {}
                  })
                }}
              >
                {buttonStatus?.data?.state}
              </Button>
            )
          )}
         

          {/* Bell, Credits, Profile */}
              <IconWrapper className="ml-4">
                <LangWrapper className="mr-1 cursor-pointer" ref={wrapperRef}>
                  <SelectLangIcon onClick={() => setOpen((prev) => !prev)} className="cursor-pointer"/>

                  <SelectedLang onClick={() => setOpen((prev) => !prev)}>
                    {langData?.data?.find((l) => l.key === language)?.short_code || "EN"}
                  </SelectedLang>
                  {open && (
                    <LanguageDropdown className="w-48 shadow-lg">
                      {langData?.data?.map((lang) => (
                        <LangMenu
                          key={lang.key}
                          onClick={() => handleSelect(lang.key)}
                          className='flex w-full items-center gap-3 rounded-lg px-3 py-2.5 justify-between'
                        >
                         <div>{lang.display_name}</div>
                         <SubMenu className='text-xs'>{lang.alt_display_name}</SubMenu>
                        </LangMenu>
                      ))}
                    </LanguageDropdown>
                  )}
                </LangWrapper>
                <NotificationButton
                  className="cursor-pointer mr-1 relative"
                  onClick={() => {
                    if (isGuestuser) {
                      setShowGuestModal(true)
                    } else if (
                      typeof window !== 'undefined' &&
                      window.openNotificationsPanel
                    ) {
                      markNotificationsAsViewed()
                      trackEvent(GA_EVENTS.notifications_opened, {
                        source: 'top_nav'
                      })
                      window.openNotificationsPanel()
                    } else {
                      markNotificationsAsViewed()
                      trackEvent(GA_EVENTS.notifications_opened, {
                        source: 'top_nav'
                      })
                      navigate(ROUTES.notifications)
                    }
                  }}
                >
                  <NotificationIcon />
                  {hasUnread && (
                    <span
                      id="header-notification-badge-desktop"
                      className="absolute top-2 right-2 w-2.5 h-2.5 bg-[#FF3B30] rounded-full pointer-events-none"
                      style={{
                        boxShadow: '0 0 0 2px #000, 0 0 6px rgba(255, 59, 48, 0.6)'
                      }}
                    />
                  )}
                </NotificationButton>
                <InfoWrapper className="cursor-pointer mr-1">
                  <FaqIcon onClick={onOpenInfoModal} />
            
                </InfoWrapper>
                <RewardWrapper className="mr-2 cursor-pointer" onClick={()=> navigate(ROUTES.wallet)}>
                 <LazyImage
                    src={credits_18}
                    alt="credits"
                  />
                  <RewardTextContainer>
                    {formatCredits(balanceCredit || 0)}
                  </RewardTextContainer>
                </RewardWrapper>


                <ProfileContainer
                  className="cursor-pointer"
                  onClick={() => onProfileClick()}
                >
                  <ProfileImage
                    src={profilePic}
                    alt="Profile"
                    isMobile={false}
                    width={40}
                    height={40}
                    onError={handleProfileImageError}
                  />

                </ProfileContainer>
              </IconWrapper>
          {showGuestModal && (
            <GuestUserModal onClose={() => setShowGuestModal(false)} />
          )}
          </div>
        </HeaderContainer>
      )}
    </>
  )
}

export default React.memo(Header);
